import {
    getBattingStyleAbbreviation,
    skillMapping,
} from "@utils/battingStyleAbbreviation";

interface PlayerMatch {
    total_matches: number;
    player_id: string;
}

interface PlayerMeta {
    player_id: string;
    "player.display_name": string;
    team_id: string;
    "team.name": string;
    "team.short_name": string;
}

interface PlayerStrikeRate {
    strike_rate?: number;
    player_id?: string;
}

interface SkillData {
    id: string;
    name: string;
    si_id: string;
}

interface ApiResponse {
    isError: boolean;
    data: {
        result: any;
    };
    statusCode: number;
}

// Define the interface for the batting data output.
export interface BattingData {
    id: string;
    batter: string;
    runs: number;
    matches: number;
    skill: string;
    team: string;
    average: number;
}

// This is the tranformer fucntion for the LeaderBoard Advanced Widgets ,
// we are extracting the required fields from the widget api ,
//for the particular key we have fetched respective data from the respective key.
export const transformMostRunsData = (data: ApiResponse) => {
    const {
        player_runs,
        player_matches,
        player_meta,
        style_data,
        sport_cricket_series_player_out_count: playerDismissalsCountMap,
    } = data?.data?.result;

    // Create a lookup for player_matches by player_id.
    const matchStatsMap: Record<string, PlayerMatch> = player_matches?.reduce(
        (acc, match) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    // Create a lookup for player_meta by player_id.
    // If there are duplicate entries, the first occurrence is used.
    const metaMap: Record<string, PlayerMeta> = player_meta?.reduce(
        (acc, meta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style?.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style?.batting_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const maxRuns = Math?.max(
        ...player_runs?.map((player: any) => player?.total_runs || 0),
    );
    const playersWithMaxRuns = player_runs
        ?.filter((player: any) => player?.total_runs === maxRuns)
        ?.map((player: any) => {
            const totalRuns = player.total_runs;
            const totalTimesDismissed =
                playerDismissalsCountMap[player?.player_id] || 1; // Avoid division by 0
            const average = Number(
                (totalRuns / totalTimesDismissed)?.toFixed(1),
            );

            return {
                player_id: player.player_id,
                average,
            };
        })
        ?.sort((a, b) => b?.average - a?.average);

    const orangeCapPlayerId = playersWithMaxRuns?.[0]?.player_id;

    const isAllZero =
        player_runs?.length > 0 &&
        player_runs?.every((player) => player.total_runs === 0);
    if (isAllZero) {
        return {
            id: "",
            batter: "-",
            team: "-",
            matches: 0,
            runs: 0,
            average: 0,
            skill: "-",
        };
    }

    // Map only those players present in player_runs.
    return player_runs
        ?.map((player) => {
            const totalRuns = player.total_runs;
            const matchStats = matchStatsMap[player?.player_id];
            const totalTimesDismissed =
                playerDismissalsCountMap[player?.player_id];

            const totalMatches = matchStats ? matchStats.total_matches : 0;
            const average =
                totalMatches > 0
                    ? Number((totalRuns / totalTimesDismissed).toFixed(1))
                    : 0;

            // Get player meta info (display name and team).
            const meta = metaMap[player?.player_id] || {};
            const batter = meta["player.display_name"] || "Unknown";
            const team = meta["team.short_name"] || "Unknown";
            const teamFlag = meta["team.image_url"] || "Unknown";

            // Since the new JSON doesn't include a skill identifier, set it to a default.
            const skillId = styleBowlDataMap[player?.player_id]?.skill_id;

            const skillBowl = getBattingStyleAbbreviation(
                styleBowlDataMap[player?.player_id]?.bowling_style,
            );

            const skillBat = getBattingStyleAbbreviation(
                styleBatDataMap[player?.player_id]?.batting_style,
            );

            const shortSkill = skillMapping(skillId, skillBat, skillBowl);
            const OrangePlayerOrNot = player?.player_id === orangeCapPlayerId;

            return {
                id: player?.player_id,
                batter,
                runs: totalRuns,
                matches: totalMatches,
                teamFlag,
                average,
                shortSkill,
                team,
                orangeCapPlayerId,
                OrangePlayerOrNot,
            };
        })
        ?.sort((a, b) => {
            if (b?.runs === a?.runs) {
                return b?.average - a?.average;
            }
            return b?.runs - a?.runs;
        });
};

export const transformStrikeRateData = (data: ApiResponse) => {
    const {
        player_runs,
        player_matches,
        player_meta,
        player_strike_rates,
        style_data,

        sport_cricket_series_player_out_count: playerDismissalsCountMap,
    } = data?.data?.result;

    // Create a lookup for player_matches by player_id.
    const matchStatsMap: Record<string, PlayerMatch> = player_matches?.reduce(
        (acc, match) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    // Create a lookup for player_meta by player_id.
    // If there are duplicate entries, the first occurrence is used.
    const metaMap: Record<string, PlayerMeta> = player_meta?.reduce(
        (acc, meta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const strikeRateMap: Record<string, PlayerStrikeRate> =
        player_strike_rates?.reduce((acc, strike_rate) => {
            acc[strike_rate?.player_id] = strike_rate;
            return acc;
        }, {} as Record<string, PlayerStrikeRate>);
    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style?.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style?.batting_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        player_runs?.length > 0 &&
        player_runs?.every((player) => player?.total_runs === 0);
    if (isAllZero) {
        return player_runs?.map(() => ({
            id: "",
            batter: "-",
            team: "-",
            matches: 0,
            runs: 0,
            average: 0,
            skill: "-",
        }));
    }

    // Map only those players present in player_runs.
    const transformedData = player_runs?.map((player) => {
        const totalRuns = player?.total_runs;
        const matchStats = matchStatsMap[player?.player_id];
        const totalTimesDismissed = playerDismissalsCountMap[player?.player_id];

        const totalMatches = matchStats ? matchStats?.total_matches : 0;
        const average =
            totalMatches > 0
                ? Number((totalRuns / totalTimesDismissed).toFixed(1))
                : 0;
        const strike =
            strikeRateMap[player?.player_id]?.strike_rate.toFixed(2) ?? 0;

        // Get player meta info (display name and team).
        const meta = metaMap[player?.player_id] || {};
        const batter = meta["player.display_name"] || "Unknown";
        const team = meta["team.short_name"] || "Unknown";
        const teamFlag = meta["team.image_url"] || "Unknown";

        const skillId = styleBowlDataMap[player?.player_id]?.skill_id;

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.player_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        return {
            id: player?.player_id,
            batter,
            runs: totalRuns,
            matches: totalMatches,
            teamFlag,
            strike,
            average,
            shortSkill,
            team,
        };
    });

    // Sort data by strike rate (descending), then by average (descending if strike rates are equal)
    return transformedData?.sort((a, b) => {
        if (b.strike === a.strike) {
            return b.average - a.average;
        }
        return b.strike - a.strike;
    });
};

export const transformBestBattingAverageData = (data: ApiResponse) => {
    const {
        player_runs,

        player_meta,

        style_data,
        sport_cricket_series_best_batting_average_batsmen: bestBattingAverage,
    } = data?.data?.result;

    // Create a lookup for player_matches by player_id.
    const matchStatsMap: Record<string, any> = player_runs?.reduce(
        (acc, match) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, any>,
    );

    // Create a lookup for player_meta by player_id.
    // If there are duplicate entries, the first occurrence is used.
    const metaMap: Record<string, PlayerMeta> = player_meta?.reduce(
        (acc, meta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );
    const averageMap: Record<string, any> = bestBattingAverage?.reduce(
        (acc, meta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style?.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style?.batting_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        player_runs?.length > 0 &&
        player_runs?.every((player) => player?.total_runs === 0);
    if (isAllZero) {
        return player_runs?.map(() => ({
            id: "",
            batter: "-",
            team: "-",
            matches: 0,
            runs: 0,
            average: 0,
            skill: "-",
        }));
    }

    // Map only those players present in player_runs.
    const transformedData = bestBattingAverage?.map((player) => {
        const matchStats = matchStatsMap[player?.player_id];

        const totalMatches = matchStats ? matchStats?.total_matches : 0;
        const runs = matchStatsMap[player?.player_id]?.total_runs;
        const average = averageMap[player?.player_id]?.average.toFixed(2);

        // Get player meta info (display name and team).
        const meta = metaMap[player?.player_id] || {};
        const batter = meta["player.display_name"] || "Unknown";
        const team = meta["team.short_name"] || "Unknown";
        const teamFlag = meta["team.image_url"] || "Unknown";

        const skillId = styleBowlDataMap[player?.player_id]?.skill_id;

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.player_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        return {
            id: player?.player_id,
            batter,
            runs,
            matches: totalMatches,
            teamFlag,

            average,
            shortSkill,
            team,
        };
    });

    // Sort data by strike rate (descending), then by average (descending if strike rates are equal)
    return transformedData?.sort((a, b) => {
        if (b.strike === a.strike) {
            return b.average - a.average;
        }
        return b.strike - a.strike;
    });
};

export const transformMostHundreds = (data: ApiResponse, dimension: any) => {
    const {
        player_runs,
        player_matches,
        player_meta,
        player_hundreds,
        style_data,
        sport_cricket_series_player_out_count: playerDismissalsCountMap,
    } = data?.data?.result;

    const matchStatsMap: Record<string, PlayerMatch> = player_matches?.reduce(
        (acc, match) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const metaMap: Record<string, PlayerMeta> = player_meta?.reduce(
        (acc, meta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const hundredsMap: Record<string, any> = player_hundreds?.reduce(
        (acc, hundreds) => {
            acc[hundreds?.player_id] = hundreds;
            return acc;
        },
        {},
    );

    // for skills
    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style?.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const transformedData = player_runs?.map((player) => {
        const totalRuns = player?.total_runs;
        const matchStats = matchStatsMap[player?.player_id];
        const totalTimesDismissed = playerDismissalsCountMap[player?.player_id];

        const totalMatches = matchStats ? matchStats.total_matches : 0;
        const average =
            totalMatches > 0
                ? Number((totalRuns / totalTimesDismissed).toFixed(1))
                : 0;

        const hundreds = hundredsMap[player?.player_id]?.hundreds || "-";

        const meta = metaMap[player?.player_id] || {};
        const batter = meta["player.display_name"] || "-";
        const team = meta["team.short_name"] || "-";
        const teamFlag = meta["team.image_url"] || "";

        // skill id to be mapped
        const skillId = styleBowlDataMap[player?.player_id]?.skill_id;

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.player_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        return {
            id: player?.player_id,
            batter,
            runs: totalRuns,
            matches: totalMatches,
            teamFlag,
            average,
            hundreds,
            shortSkill,
            team,
        };
    });

    // Sort by most hundreds first, then by runs as a tiebreaker
    transformedData?.sort((a, b) =>
        b.hundreds !== a.hundreds ? b.hundreds - a.hundreds : b.runs - a.runs,
    );

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    if (dimension.width > 640) {
        while (transformedData?.length < 3) {
            transformedData?.push({
                id: "",
                batter: "-",
                runs: "-",
                matches: "-",
                teamFlag: "",
                average: "-",
                hundreds: "-",
                team: "-",
            });
        }
        return transformedData?.slice(0, 3);
    }
    return transformedData;
};
export const transformMostFiftees = (data: ApiResponse) => {
    const {
        player_runs,
        player_matches,
        player_meta,
        player_fifties,
        style_data,
        sport_cricket_series_player_out_count: playerDismissalsCountMap,
    } = data?.data?.result;

    const matchStatsMap: Record<string, PlayerMatch> = player_matches?.reduce(
        (acc, match) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const metaMap: Record<string, PlayerMeta> = player_meta?.reduce(
        (acc, meta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const fiftiesMap: Record<string, any> = player_fifties?.reduce(
        (acc, fifties) => {
            acc[fifties?.player_id] = fifties;
            return acc;
        },
        {},
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style?.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style?.batting_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const transformedData = player_runs?.map((player) => {
        const totalRuns = player?.total_runs;
        const matchStats = matchStatsMap[player?.player_id];
        const totalTimesDismissed = playerDismissalsCountMap[player?.player_id];

        const totalMatches = matchStats ? matchStats.total_matches : 0;
        const average =
            totalMatches > 0
                ? Number((totalRuns / totalTimesDismissed).toFixed(1))
                : 0;

        const fifties = fiftiesMap[player?.player_id]?.fifties || "-";

        const meta = metaMap[player?.player_id] || {};
        const batter = meta["player.display_name"] || "-";
        const team = meta["team.short_name"] || "-";
        const teamFlag = meta["team.image_url"] || "";

        const skillId = styleBowlDataMap[player?.player_id]?.skill_id;

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.player_id]?.batting_style,
        );
        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        return {
            id: player?.player_id,
            batter,
            runs: totalRuns,
            matches: totalMatches,
            teamFlag,
            average,
            fifties,
            shortSkill,
            team,
        };
    });

    // Sort by most hundreds first, then by runs as a tiebreaker
    transformedData.sort((a, b) =>
        b.fifties !== a.fifties ? b.fifties - a.fifties : b.runs - a.runs,
    );

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    while (transformedData.length < 3) {
        transformedData.push({
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",
            fifties: "-",
            team: "-",
        });
    }

    return transformedData.slice(0, 3);
};

export const transformMostSixes = (data: ApiResponse) => {
    const { player_sixes, style_data } = data?.data?.result;

    const metaMap: Record<string, PlayerMeta> = player_sixes?.reduce(
        (acc, meta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style?.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style?.batting_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const transformedData = player_sixes?.map((player) => {
        const meta = metaMap[player?.player_id] || {};
        const batter = meta["player.display_name"] || "-";
        const team = meta["team.short_name"] || "-";
        const teamFlag = meta["team.image_url"] || "";

        const sixes = player?.total_sixes;
        const runs = player?.total_runs;
        const matches = player?.total_matches;
        const skillId = styleBowlDataMap[player?.player_id]?.skill_id;

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.player_id]?.batting_style,
        );
        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        return {
            id: player?.player_id,
            batter,
            sixes,
            teamFlag,
            matches,
            runs,

            shortSkill,
            team,
        };
    });

    // Sort by most hundreds first, then by runs as a tiebreaker
    transformedData.sort((a, b) =>
        b.sixes !== a.sixes ? b.sixes - a.sixes : b.runs - a.runs,
    );

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    while (transformedData.length < 3) {
        transformedData.push({
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            sixes: "-",
            shortSkill: "-",
            team: "-",
        });
    }

    return transformedData.slice(0, 3);
};

export const transformMostFours = (data: ApiResponse) => {
    const { player_fours, style_data } = data?.data?.result;

    const matchStatsMap: Record<string, PlayerMatch> = player_fours?.reduce(
        (acc, match) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const metaMap: Record<string, PlayerMeta> = player_fours?.reduce(
        (acc, meta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style?.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style?.batting_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const transformedData = player_fours?.map((player) => {
        const meta = metaMap[player?.player_id] || {};
        const batter = meta["player.display_name"] || "-";
        const team = meta["team.short_name"] || "-";
        const teamFlag = meta["team.image_url"] || "";

        const fours = player?.total_fours;
        const runs = player?.total_runs;
        const matches = player?.total_matches;
        const skillId = styleBowlDataMap[player?.player_id]?.skill_id;

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.player_id]?.batting_style,
        );
        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        return {
            id: player?.player_id,
            batter,
            fours,
            teamFlag,
            matches,
            runs,

            shortSkill,
            team,
        };
    });

    // Sort by most hundreds first, then by runs as a tiebreaker
    transformedData.sort((a, b) =>
        b.fours !== a.fours ? b.fours - a.fours : b.runs - a.runs,
    );

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    while (transformedData.length < 3) {
        transformedData.push({
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            fours: "-",
            shortSkill: "-",
            team: "-",
        });
    }

    return transformedData.slice(0, 3);
};

export const transformHighestIndividualRunsData = (data: ApiResponse) => {
    const {
        player_matches,
        player_meta,
        player_strike_rates,
        player_highest_scores,
        style_data,
        sport_cricket_series_orange_cap_batsmen,
        sport_cricket_series_player_out_count: playerDismissalsCountMap,
    } = data?.data?.result;

    // Create a lookup for player_matches by player_id.
    const matchStatsMap: Record<string, PlayerMatch> = player_matches?.reduce(
        (acc, match) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const highestIndividualMap: Record<string, any> =
        player_highest_scores?.reduce((acc, highest) => {
            acc[highest?.player_id] = highest;
            return acc;
        }, {} as Record<string, any>);

    const metaMap: Record<string, PlayerMeta> = player_meta?.reduce(
        (acc, meta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const strikeRateMap: Record<string, PlayerStrikeRate> =
        player_strike_rates?.reduce((acc, strike_rate) => {
            acc[strike_rate?.player_id] = strike_rate;
            return acc;
        }, {} as Record<string, PlayerStrikeRate>);
    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style?.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style?.batting_style_text,
                player_id: style?.player_id,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const playerWithOrangeCap =
        sport_cricket_series_orange_cap_batsmen.result.orange_cap[0].player_id;

    const isAllZero =
        player_highest_scores?.length > 0 &&
        player_highest_scores?.every((player) => player.highest_score === 0);
    if (isAllZero) {
        return {
            id: "",
            batter: "-",
            team: "-",
            matches: 0,
            runs: 0,
            strike: 0,
            skill: "-",
            highestIndividual: "-",
        };
    }

    // Map only those players present in player_runs.
    return player_highest_scores
        ?.map((player) => {
            const matchStats = matchStatsMap[player?.player_id];

            // const totalTimesDismissed = playerDismissalsCountMap[player?.player_id];

            const totalMatches = matchStats ? matchStats.total_matches : 0;

            const strike =
                strikeRateMap[player?.player_id]?.strike_rate.toFixed(2) ?? 0;
            const highestIndividual = player.highest_score;

            // Get player meta info (display name and team).
            const meta = metaMap[player?.player_id] || {};

            const batter = meta["player.display_name"] || "Unknown";
            const team = meta["team.short_name"] || "Unknown";
            const teamFlag = meta["team.image_url"] || "Unknown";

            const skillId = styleBowlDataMap[player?.player_id]?.skill_id;

            const skillBowl = getBattingStyleAbbreviation(
                styleBowlDataMap[player?.player_id]?.bowling_style,
            );

            const skillBat = getBattingStyleAbbreviation(
                styleBatDataMap[player?.player_id]?.batting_style,
            );

            const shortSkill = skillMapping(skillId, skillBat, skillBowl);

            const OrangePlayerOrNot = player?.player_id === playerWithOrangeCap;

            return {
                id: player?.player_id,
                batter,
                matches: totalMatches,
                teamFlag,
                highestIndividual,
                strike,
                shortSkill,
                team,
                OrangePlayerOrNot,
            };
        })
        ?.sort((a, b) => {
            if (b?.highestIndividual === a?.highestIndividual) {
                return b?.strike - a?.strike; // sort by strike rate if scores are same
            }
            return b?.highestIndividual - a?.highestIndividual; // otherwise sort by score
        });
};

export const transformWicketData = (data: ApiResponse): any => {
    //@ts-ignore
    const {
        player_wickets,
        player_matches,
        player_meta,
        skill_data,
        player_runs_given,
        player_economy,
        style_data,
    } = data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = player_matches.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const economyRateMap: Record<string, any> = player_economy?.reduce(
        (acc, economy) => {
            acc[economy?.player_id] = economy;
            return acc;
        },
        {} as Record<string, any>,
    );

    const metaMap = player_meta.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const runsMap = player_runs_given.reduce(
        (
            acc: Record<string, number>,
            runData: { total_runs: number; player_id: string },
        ) => {
            acc[runData?.player_id] = runData.total_runs;
            return acc;
        },
        {} as Record<string, number>,
    );

    const skillMap = skill_data.reduce(
        (acc: Record<string, string>, skill: SkillData) => {
            if (skill.si_id) {
                acc[skill.si_id] = skill.name;
            }
            return acc;
        },
        {} as Record<string, string>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
                skill_id: style.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const maxWickets = Math?.max(
        ...player_wickets?.map((player: any) => player.total_wickets || 0),
    );
    const purplePlayerId = player_wickets
        ?.map((player: any) => {
            const wickets = player?.total_wickets || 0;
            const runsGiven = runsMap[player?.player_id] || 0;
            const average =
                wickets > 0 ? Number((runsGiven / wickets).toFixed(1)) : 0;

            return {
                player_id: player.player_id,
                total_wickets: wickets,
                average,
            };
        })
        ?.sort((a, b) => {
            // Sort by wickets DESC, then average ASC
            if (b.total_wickets !== a.total_wickets) {
                return b.total_wickets - a.total_wickets;
            }
            return a.average - b.average;
        })?.[0]?.player_id;

    const isAllZero =
        player_wickets?.length > 0 &&
        player_wickets?.every((player) => player?.total_wickets === 0);

    if (isAllZero) {
        return player_wickets?.map((player: any) => {
            const matchStats = matchStatsMap[player?.player_id] || {
                total_matches: 0,
            };
            const totalMatches = matchStats.total_matches || 0;
            const wickets = player.total_wickets || 0;
            const runsGiven = runsMap[player?.player_id] || 0;

            // Calculate average as total runs given / total wickets taken.
            const average =
                wickets > 0 ? Number((runsGiven / wickets).toFixed(1)) : 0;

            const economy = economyRateMap[player?.player_id].economy;

            // Get player meta information.
            const meta = metaMap[player?.player_id] || ({} as PlayerMeta);
            const batter = meta["player.display_name"] || "Unknown";
            const team = meta["team.short_name"] || "Unknown";

            // Default skill to 'Unknown' since no skill info is provided.
            const skill = "Unknown";
            return {
                batter: "-",
                team: "-",
                matches: 0,
                wickets: 0,
                average: 0,
                skill: "-",
            };
        });
    }

    // Transform only those players present in player_wickets.
    return player_wickets
        ?.map((player: any) => {
            const matchStats = matchStatsMap[player?.player_id] || {
                total_matches: 0,
            };
            const totalMatches = matchStats?.total_matches || 0;
            const wickets = player?.total_wickets || 0;
            const runsGiven = runsMap[player?.player_id] || 0;

            // Calculate average as total runs given / total wickets taken.
            const average =
                wickets > 0 ? Number((runsGiven / wickets).toFixed(1)) : 0;

            const economy = economyRateMap[player?.player_id]?.economy;

            // Get player meta information.
            const meta = metaMap[player?.player_id] || ({} as PlayerMeta);
            const batter = meta["player.display_name"] || "Unknown";
            const team = meta["team.short_name"] || "Unknown";
            const teamFlag = meta["team.image_url"] || "Unknown";

            // Default skill to 'Unknown' since no skill info is provided.
            const skillId = styleBowlDataMap[player?.player_id]?.skill_id;

            const skillBowl = getBattingStyleAbbreviation(
                styleBowlDataMap[player?.player_id]?.bowling_style,
            );

            const skillBat = getBattingStyleAbbreviation(
                styleBatDataMap[player?.player_id]?.batting_style,
            );

            const shortSkill = skillMapping(skillId, skillBat, skillBowl);

            const purplePlayerOrNot = player?.player_id === purplePlayerId;

            return {
                id: player?.player_id,
                batter,
                wickets,
                matches: totalMatches,
                average,
                economy,
                shortSkill,
                team,
                teamFlag,
                purplePlayerOrNot,
                purplePlayerId,
            };
        })
        .sort((a, b) => {
            // First sort by wickets (desc), then economy (asc)
            if (b.wickets !== a.wickets) {
                return b.wickets - a.wickets;
            }
            return a.average - b.average;
        });
};
export const transformEconomyData = (data: ApiResponse): any => {
    //@ts-ignore
    const { player_economy, style_data } = data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = player_economy.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const economyRateMap: Record<string, any> = player_economy?.reduce(
        (acc, economy) => {
            acc[economy?.player_id] = economy;
            return acc;
        },
        {} as Record<string, any>,
    );

    const metaMap = player_economy?.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        player_economy.length > 0 &&
        player_economy.every((player) => player.economy === 0);

    if (isAllZero) {
        return player_economy.map((player: any) => {
            const matchStats = matchStatsMap[player?.player_id] || {
                total_matches: 0,
            };
            const totalMatches = matchStats.total_matches || 0;
            const wickets = player.total_wickets || 0;

            const economy =
                economyRateMap[player?.player_id]?.economy.toFixed(2);

            // Get player meta information.
            const meta = metaMap[player?.player_id] || ({} as PlayerMeta);

            const batter = meta["player.display_name"] || "Unknown";
            const team = meta["team.short_name"] || "Unknown";

            // Default skill to 'Unknown' since no skill info is provided.
            const skill = "Unknown";
            return {
                batter: "-",
                team: "-",
                matches: 0,
                wickets: 0,
                average: 0,
                skill: "-",
            };
        });
    }

    // Transform only those players present in player_wickets.
    return player_economy
        ?.map((player: any) => {
            const matchStats = matchStatsMap[player?.player_id] || {
                total_matches: 0,
            };
            const totalMatches = matchStats.total_matches || 0;
            const wickets = player.total_wickets || 0;
            // const runsGiven = runsMap[player?.player_id] || 0;

            // Calculate average as total runs given / total wickets taken.
            //const average = wickets > 0 ? Number((runsGiven / wickets).toFixed(1)) : 0;

            const economy =
                economyRateMap[player?.player_id]?.economy.toFixed(2);

            // Get player meta information.
            const meta = metaMap[player?.player_id] || ({} as PlayerMeta);
            const batter = meta["player.display_name"] || "Unknown";
            const team = meta["team.short_name"] || "Unknown";
            const teamFlag = meta["team.image_url"];
            // Default skill to 'Unknown' since no skill info is provided.
            const skillId = meta["player.skill_id"];

            const skillBowl = getBattingStyleAbbreviation(
                styleBowlDataMap[player?.player_id]?.bowling_style,
            );

            const skillBat = getBattingStyleAbbreviation(
                styleBatDataMap[player?.player_id]?.batting_style,
            );

            const shortSkill = skillMapping(skillId, skillBat, skillBowl);

            //  const purplePlayerOrNot = player?.player_id === purplePlayerId;

            return {
                id: player?.player_id,
                batter,
                wickets,
                matches: totalMatches,
                //  average,
                economy,
                shortSkill,
                team,
                teamFlag,
                //   purplePlayerOrNot,
                //    purplePlayerId,
            };
        })
        ?.sort((a, b) => {
            if (a?.economy === b?.economy) {
                return b?.wickets - a?.wickets; // Descending order for wickets
            }
            return a?.economy - b?.economy; // Ascending order for economy
        });
};

export const transformStrikeData = (data: ApiResponse): any => {
    //@ts-ignore
    const { player_economy, style_data } = data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = player_economy.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const strikeRateMap: Record<string, any> = player_economy?.reduce(
        (acc, economy) => {
            acc[economy?.player_id] = economy;
            return acc;
        },
        {} as Record<string, any>,
    );

    const metaMap = player_economy?.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        player_economy.length > 0 &&
        player_economy.every((player) => player.economy === 0);

    if (isAllZero) {
        return player_economy.map((player: any) => {
            const matchStats = matchStatsMap[player?.player_id] || {
                total_matches: 0,
            };
            const totalMatches = matchStats.total_matches || 0;
            const wickets = player.total_wickets || 0;

            const strike = strikeRateMap[player?.player_id]?.strike_rate;

            // Get player meta information.
            const meta = metaMap[player?.player_id] || ({} as PlayerMeta);

            const batter = meta["player.display_name"] || "Unknown";
            const team = meta["team.short_name"] || "Unknown";

            // Default skill to 'Unknown' since no skill info is provided.
            const skill = "Unknown";
            return {
                batter: "-",
                team: "-",
                matches: 0,
                wickets: 0,
                average: 0,
                skill: "-",
            };
        });
    }

    // Transform only those players present in player_wickets.
    return player_economy?.map((player: any) => {
        const matchStats = matchStatsMap[player?.player_id] || {
            total_matches: 0,
        };
        const totalMatches = matchStats.total_matches || 0;
        const wickets = player.total_wickets || 0;
        // const runsGiven = runsMap[player?.player_id] || 0;

        // Calculate average as total runs given / total wickets taken.
        //const average = wickets > 0 ? Number((runsGiven / wickets).toFixed(1)) : 0;

        const strike = strikeRateMap[player?.player_id]?.strike_rate.toFixed(2);

        // Get player meta information.
        const meta = metaMap[player?.player_id] || ({} as PlayerMeta);
        const batter = meta["player.display_name"] || "Unknown";
        const team = meta["team.short_name"] || "Unknown";
        const teamFlag = meta["team.image_url"];
        // Default skill to 'Unknown' since no skill info is provided.
        const skillId = meta["player.skill_id"];

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.player_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        //  const purplePlayerOrNot = player?.player_id === purplePlayerId;

        return {
            id: player?.player_id,
            batter,
            wickets,
            matches: totalMatches,
            //  average,
            strike,
            shortSkill,
            team,
            teamFlag,
            //   purplePlayerOrNot,
            //    purplePlayerId,
        };
    });
};

export const transformMostFiveWicketData = (
    data: ApiResponse,
    dimension: any,
): any => {
    //@ts-ignore
    const {
        player_average,
        player_most_five_wickets,
        player_wickets,
        style_data,
    } = data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = player_average.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const averageRateMap: Record<string, any> = player_average?.reduce(
        (acc, average) => {
            acc[average?.player_id] = average;
            return acc;
        },
        {} as Record<string, any>,
    );

    const wicketsMap: Record<string, any> = player_wickets?.reduce(
        (acc, fifties) => {
            acc[fifties?.player_id] = fifties;
            return acc;
        },
        {},
    );

    const metaMap = player_average?.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        player_most_five_wickets?.length > 0 &&
        player_most_five_wickets.every((player) => player.total_matches === 0);

    if (isAllZero) {
        return {
            batter: "-",
            team: "-",
            matches: 0,
            wickets: 0,
            average: 0,
            skill: "-",
        };
    }

    // Transform only those players present in player_wickets.
    const transformedData = player_most_five_wickets
        ?.map((player: any) => {
            const matchStats = matchStatsMap[player?.player_id] || {
                total_matches: 0,
            };
            const totalMatches = matchStats?.total_matches || 0;

            const average =
                averageRateMap[player?.player_id]?.average.toFixed(2);
            const wickets = wicketsMap[player?.player_id]?.total_wickets;

            const fiveWickets = player?.total_matches;

            // Get player meta information.
            const meta = metaMap[player?.player_id] || ({} as PlayerMeta);
            const batter = meta["player.display_name"] || "Unknown";
            const team = meta["team.short_name"] || "Unknown";
            const teamFlag = meta["team.image_url"];
            // Default skill to 'Unknown' since no skill info is provided.
            const skillId = meta["player.skill_id"];

            const skillBowl = getBattingStyleAbbreviation(
                styleBowlDataMap[player?.player_id]?.bowling_style,
            );

            const skillBat = getBattingStyleAbbreviation(
                styleBatDataMap[player?.player_id]?.batting_style,
            );

            const shortSkill = skillMapping(skillId, skillBat, skillBowl);

            return {
                id: player?.player_id,
                batter,
                wickets,
                matches: totalMatches,
                fiveWickets,
                average,
                shortSkill,
                team,
                teamFlag,
                //   purplePlayerOrNot,
                //    purplePlayerId,
            };
        })
        .sort((a, b) => {
            if (b.fiveWickets === a.fiveWickets) {
                return (b.wickets ?? 0) - (a.wickets ?? 0);
            }
            return (b.fiveWickets ?? 0) - (a.fiveWickets ?? 0);
        });

    // Ensure top three players are returned, filling with '-' if fewer than three exist

    if (dimension.width > 640) {
        while (transformedData?.length < 3) {
            transformedData?.push({
                id: "",
                batter: "-",
                runs: "-",
                matches: "-",
                teamFlag: "",
                average: "-",
                fifties: "-",
                team: "-",
            });
        }

        return transformedData?.slice(0, 3);
    }
    return transformedData;
};

export const transformMostFourWicketData = (data: ApiResponse): any => {
    //@ts-ignore
    const {
        player_average,
        player_most_four_wickets,
        style_data,
        player_wickets,
    } = data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = player_average?.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const averageRateMap: Record<string, any> = player_average?.reduce(
        (acc, average) => {
            acc[average?.player_id] = average;
            return acc;
        },
        {} as Record<string, any>,
    );
    const fiveWicketMap: Record<string, any> = player_most_four_wickets?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.player_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );
    const metaMap = player_average?.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );
    const wicketsMap: Record<string, any> = player_wickets?.reduce(
        (acc, fifties) => {
            acc[fifties?.player_id] = fifties;
            return acc;
        },
        {},
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        player_most_four_wickets?.length > 0 &&
        player_most_four_wickets.every((player) => player?.total_matches === 0);

    if (isAllZero) {
        return {
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",
            fifties: "-",
            team: "-",
        };
    }

    // Transform only those players present in player_wickets.
    const transformedData = player_average?.map((player: any) => {
        const matchStats = matchStatsMap[player?.player_id] || {
            total_matches: 0,
        };
        const totalMatches = matchStats.total_matches || 0;

        const average = averageRateMap[player?.player_id]?.average.toFixed(2);

        const fiveWickets = fiveWicketMap[player?.player_id].total_matches;
        const wickets = wicketsMap[player?.player_id]?.total_wickets;

        // Get player meta information.
        const meta = metaMap[player?.player_id] || ({} as PlayerMeta);
        const batter = meta["player.display_name"] || "Unknown";
        const team = meta["team.short_name"] || "Unknown";
        const teamFlag = meta["team.image_url"];
        // Default skill to 'Unknown' since no skill info is provided.
        const skillId = meta["player.skill_id"];

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.player_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        return {
            id: player?.player_id,
            batter,
            wickets,
            matches: totalMatches,
            fiveWickets,
            average,
            shortSkill,
            team,
            teamFlag,
            //   purplePlayerOrNot,
            //    purplePlayerId,
        };
    });
    transformedData.sort((a, b) => {
        if (b.fiveWickets === a.fiveWickets) {
            return (b.wickets ?? 0) - (a.wickets ?? 0);
        }
        return (b.fiveWickets ?? 0) - (a.fiveWickets ?? 0);
    });

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    while (transformedData.length < 3) {
        transformedData.push({
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",
            fifties: "-",
            team: "-",
        });
    }

    return transformedData?.slice(0, 3);
};

export const transformBestBowlingFiguresData = (data: ApiResponse): any => {
    //@ts-ignore
    const { player_average, best_bowling_figures, style_data, total_matches } =
        data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = best_bowling_figures?.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const wicketsMap: Record<string, any> = total_matches?.reduce(
        (acc, average) => {
            acc[average?.player_id] = average;
            return acc;
        },
        {} as Record<string, any>,
    );
    const bestBowlingFigure: Record<string, any> = best_bowling_figures?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.player_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );
    const metaMap = best_bowling_figures?.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        best_bowling_figures?.length > 0 &&
        best_bowling_figures.every((player) => player?.total_matches === 0);

    if (isAllZero) {
        return {
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",
            fifties: "-",
            team: "-",
        };
    }

    // Transform only those players present in player_wickets.
    const transformedData = best_bowling_figures?.map((player: any) => {
        const matchStats = matchStatsMap[player?.player_id] || {
            total_matches: 0,
        };

        const totalMatches = wicketsMap[player?.player_id]?.total_matches;

        const wickets = wicketsMap[player?.player_id]?.total_wickets;

        const bestBowlingWickets = bestBowlingFigure[player?.player_id].wickets;
        const bestBowlingRuns = bestBowlingFigure[player?.player_id].runs;
        const BBI = `${bestBowlingRuns}/${bestBowlingWickets}`;

        // Get player meta information.
        const meta = metaMap[player?.player_id] || ({} as PlayerMeta);
        const batter = meta["player.display_name"] || "Unknown";
        const team = meta["team.short_name"] || "Unknown";
        const teamFlag = meta["team.image_url"];
        // Default skill to 'Unknown' since no skill info is provided.
        const skillId = meta["player.skill_id"];

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.player_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        //  const purplePlayerOrNot = player?.player_id === purplePlayerId;

        return {
            id: player?.player_id,
            batter,
            wickets,
            matches: totalMatches,
            BBI,

            shortSkill,
            team,
            teamFlag,
            //   purplePlayerOrNot,
            //    purplePlayerId,
        };
    });

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    while (transformedData.length < 3) {
        transformedData.push({
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",
            fifties: "-",
            team: "-",
        });
    }

    return transformedData?.slice(0, 3);
};

export const transformMostDismissals = (data: ApiResponse): any => {
    //@ts-ignore
    const { most_dismissals, wicket_keepers, style_data, total_matches } =
        data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = wicket_keepers?.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const mostDismissalsMap: Record<string, any> = most_dismissals?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.fielder_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );
    const metaMap = wicket_keepers?.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );
    const totalMatchesMap: Record<string, any> = total_matches?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.player_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        most_dismissals?.length > 0 &&
        most_dismissals.every((player) => player?.total_wickets === 0);

    if (isAllZero) {
        return {
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",
            fifties: "-",
            team: "-",
        };
    }

    // Transform only those players present in player_wickets.
    const transformedData = most_dismissals?.map((player: any) => {
        const matchStats = matchStatsMap[player?.fielder_id] || {
            total_matches: 0,
        };
        const totalMatches = totalMatchesMap[player?.fielder_id]?.total_matches;

        const dismissals = mostDismissalsMap[player?.fielder_id]?.total_wickets;

        // Get player meta information.
        const meta = metaMap[player?.fielder_id] || ({} as PlayerMeta);
        const batter = meta["player.display_name"] || "Unknown";
        const team = meta["team.short_name"] || "Unknown";
        const teamFlag = meta["team.image_url"];
        // Default skill to 'Unknown' since no skill info is provided.
        const skillId = meta["player.skill_id"];

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.fielder_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.fielder_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        //  const purplePlayerOrNot = player?.player_id === purplePlayerId;

        return {
            id: player?.fielder_id,
            batter,

            matches: totalMatches,
            dismissals,

            shortSkill,
            team,
            teamFlag,
            //   purplePlayerOrNot,
            //    purplePlayerId,
        };
    });

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    while (transformedData?.length < 3) {
        transformedData.push({
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",

            team: "-",
        });
    }

    return transformedData?.slice(0, 3);
};

export const transformMostDismissalsInIPL = (data: ApiResponse): any => {
    //@ts-ignore
    const { most_dismissals, wicket_keepers, style_data, total_matches } =
        data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = wicket_keepers?.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const mostDismissalsMap: Record<string, any> = most_dismissals?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.fielder_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );
    const metaMap = wicket_keepers?.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );
    const totalMatchesMap: Record<string, any> = total_matches?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.player_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        most_dismissals?.length > 0 &&
        most_dismissals.every((player) => player?.total_wickets === 0);

    if (isAllZero) {
        return {
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",
            fifties: "-",
            team: "-",
        };
    }

    // Transform only those players present in player_wickets.
    const transformedData = most_dismissals?.map((player: any) => {
        const matchStats = matchStatsMap[player?.fielder_id] || {
            total_matches: 0,
        };
        const totalMatches = totalMatchesMap[player?.fielder_id]?.total_matches;

        const dismissals = mostDismissalsMap[player?.fielder_id]?.total_wickets;

        // Get player meta information.
        const meta = metaMap[player?.fielder_id] || ({} as PlayerMeta);
        const batter = meta["player.display_name"] || "Unknown";
        const team = meta["team.short_name"] || "Unknown";
        const teamFlag = meta["team.image_url"];
        // Default skill to 'Unknown' since no skill info is provided.
        const skillId = meta["player.skill_id"];

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.fielder_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.fielder_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        //  const purplePlayerOrNot = player?.player_id === purplePlayerId;

        return {
            id: player?.fielder_id,
            batter,

            matches: totalMatches,
            dismissals,

            shortSkill,
            team,
            teamFlag,
            //   purplePlayerOrNot,
            //    purplePlayerId,
        };
    });

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    while (transformedData.length < 3) {
        transformedData.push({
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",

            team: "-",
        });
    }

    return transformedData?.slice(0, 3);
};

export const transformMostCatches = (data: ApiResponse): any => {
    //@ts-ignore
    const { most_catches, fielders, style_data, total_matches } =
        data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = fielders?.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const mostDismissalsMap: Record<string, any> = most_catches?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.fielder_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );
    const metaMap = fielders?.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );
    const totalMatchesMap: Record<string, any> = total_matches?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.player_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
                skill_id: style.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        most_catches?.length > 0 &&
        most_catches.every((player) => player?.total_wickets === 0);

    if (isAllZero) {
        return {
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",
            fifties: "-",
            team: "-",
        };
    }

    // Transform only those players present in player_wickets.
    const transformedData = most_catches?.map((player: any) => {
        const matchStats = matchStatsMap[player?.fielder_id] || {
            total_matches: 0,
        };
        const totalMatches = totalMatchesMap[player?.fielder_id]?.total_matches;

        const catches = mostDismissalsMap[player?.fielder_id]?.total_wickets;

        // Get player meta information.
        const meta = metaMap[player?.fielder_id] || ({} as PlayerMeta);
        const batter = meta["player.display_name"] || "Unknown";
        const team = meta["team.short_name"] || "Unknown";
        const teamFlag = meta["team.image_url"];
        // Default skill to 'Unknown' since no skill info is provided.

        const skillId = meta["player.skill_id"];

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.fielder_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.fielder_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        return {
            id: player?.fielder_id,
            batter,

            matches: totalMatches,
            catches,

            shortSkill,
            team,
            teamFlag,
            //   purplePlayerOrNot,
            //    purplePlayerId,
        };
    });

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    while (transformedData.length < 3) {
        transformedData.push({
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",

            team: "-",
        });
    }

    return transformedData?.slice(0, 3);
};

export const transformMostCatchesInIPL = (data: ApiResponse): any => {
    //@ts-ignore
    const { most_catches, fielders, style_data, total_matches } =
        data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = fielders?.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const mostDismissalsMap: Record<string, any> = most_catches?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.fielder_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );
    const metaMap = fielders?.reduce(
        (acc: Record<string, PlayerMeta>, meta: PlayerMeta) => {
            if (!acc[meta?.player_id]) {
                acc[meta?.player_id] = meta;
            }
            return acc;
        },
        {} as Record<string, PlayerMeta>,
    );
    const totalMatchesMap: Record<string, any> = total_matches?.reduce(
        (acc, fiveWicket) => {
            acc[fiveWicket?.player_id] = fiveWicket;
            return acc;
        },
        {} as Record<string, any>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
                skill_id: style.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
                skill_id: style.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const isAllZero =
        most_catches?.length > 0 &&
        most_catches.every((player) => player?.total_wickets === 0);

    if (isAllZero) {
        return {
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",
            fifties: "-",
            team: "-",
        };
    }

    // Transform only those players present in player_wickets.
    const transformedData = most_catches?.map((player: any) => {
        const matchStats = matchStatsMap[player?.fielder_id] || {
            total_matches: 0,
        };
        const totalMatches = totalMatchesMap[player?.fielder_id]?.total_matches;

        const catches = mostDismissalsMap[player?.fielder_id]?.total_wickets;

        // Get player meta information.
        const meta = metaMap[player?.fielder_id] || ({} as PlayerMeta);
        const batter = meta["player.display_name"] || "Unknown";
        const team = meta["team.short_name"] || "Unknown";
        const teamFlag = meta["team.image_url"];
        // Default skill to 'Unknown' since no skill info is provided.

        const skillId = meta["player.skill_id"];

        const skillBowl = getBattingStyleAbbreviation(
            styleBowlDataMap[player?.player_id]?.bowling_style,
        );

        const skillBat = getBattingStyleAbbreviation(
            styleBatDataMap[player?.fielder_id]?.batting_style,
        );

        const shortSkill = skillMapping(skillId, skillBat, skillBowl);

        return {
            id: player?.fielder_id,
            batter,

            matches: totalMatches,
            catches,

            shortSkill,
            team,
            teamFlag,
            //   purplePlayerOrNot,
            //    purplePlayerId,
        };
    });

    // Ensure top three players are returned, filling with '-' if fewer than three exist
    while (transformedData.length < 3) {
        transformedData.push({
            id: "",
            batter: "-",
            runs: "-",
            matches: "-",
            teamFlag: "",
            average: "-",

            team: "-",
        });
    }

    return transformedData?.slice(0, 3);
};

export const transformMaidenBowlers = (
    data: ApiResponse,
    dimension: any,
): any => {
    //@ts-ignore
    const {
        player_wickets,
        player_matches,
        player_meta,
        skill_data,
        style_data,

        player_maidens,
    } = data?.data?.result;

    // Build lookup maps for match stats, player meta, runs given, and skills.
    const matchStatsMap = player_matches.reduce(
        (acc: Record<string, PlayerMatch>, match: PlayerMatch) => {
            acc[match?.player_id] = match;
            return acc;
        },
        {} as Record<string, PlayerMatch>,
    );

    const mostMaidenMap: Record<string, any> = player_maidens?.reduce(
        (acc, maiden) => {
            acc[maiden?.bowler_ids] = maiden;
            return acc;
        },
        {} as Record<string, any>,
    );

    const wicketsMap: Record<string, any> = player_wickets?.reduce(
        (acc, maiden) => {
            acc[maiden?.player_id] = maiden;
            return acc;
        },
        {} as Record<string, any>,
    );

    const styleBowlDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                bowling_style: style.bowling_style_text,
                player_id: style?.player_id,
                short_display_name: style?.short_display_name,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );
    const styleBatDataMap: Record<string, any> = style_data?.reduce(
        (acc, style) => {
            acc[style?.player_id] = {
                batting_style: style.batting_style_text,
                player_id: style?.player_id,
                short_display_name: style?.short_display_name,
                skill_id: style?.player_skill_id,
            };
            return acc;
        },
        {},
    );

    const maxWickets = Math?.max(
        ...player_maidens?.map((player: any) => player.total_wickets || 0),
    );
    const purplePlayerId = player_wickets?.find(
        (player: any) => player.total_wickets === maxWickets,
    )?.player_id;

    const isAllZero =
        player_maidens?.length > 0 &&
        player_maidens?.every((player) => player.total_maidens === 0);

    if (isAllZero) {
        return player_maidens.map((player: any) => {
            const matchStats = matchStatsMap[player?.player_id] || {
                total_matches: 0,
            };
            const totalMatches = matchStats.total_matches || 0;
            const wickets = player.total_wickets || 0;

            // Get player meta information.
            const meta =
                mostMaidenMap[player?.bowler_ids] || ({} as PlayerMeta);

            const batter = meta["player.display_name"] || "Unknown";
            const team = meta["team.short_name"] || "Unknown";

            // Default skill to 'Unknown' since no skill info is provided.
            const skill = "Unknown";
            return {
                batter: "-",
                team: "-",
                matches: 0,
                wickets: 0,
                average: 0,
                skill: "-",
            };
        });
    }

    // Transform only those players present in player_wickets.
    const transformedData = player_maidens
        ?.map((player: any) => {
            const matchStats = matchStatsMap[player?.bowler_ids] || {
                total_matches: 0,
            };
            const totalMatches = matchStats?.total_matches || 0;
            const wickets = wicketsMap[player?.bowler_ids]?.total_wickets || 0;

            const maidens = player?.total_maidens;

            const meta =
                mostMaidenMap[player?.bowler_ids] || ({} as PlayerMeta);
            const batter =
                styleBowlDataMap[player?.bowler_ids]?.short_display_name ||
                "Unknown";
            const team = meta["bowling_team.short_name"] || "Unknown";
            const teamFlag = meta["bowling_team.image_url"];

            const skillId = styleBowlDataMap[player?.bowler_ids]?.skill_id;

            const skillBowl = getBattingStyleAbbreviation(
                styleBowlDataMap[player?.bowler_ids]?.bowling_style,
            );

            const skillBat = getBattingStyleAbbreviation(
                styleBatDataMap[player?.player_id]?.batting_style,
            );

            const shortSkill = skillMapping(skillId, skillBat, skillBowl);

            return {
                id: player?.bowler_ids,
                batter,
                wickets,
                matches: totalMatches,
                teamFlag,
                shortSkill,
                maidens,
                team,
            };
        })
        ?.sort((a, b) => {
            if (a?.maidens === b?.maidens) {
                return b?.wickets - a?.wickets; // Descending order for wickets
            }
            return b?.maidens - a?.maidens; // Ascending order for economy
        });

    if (dimension.width > 640) {
        while (transformedData?.length < 3) {
            transformedData?.push({
                id: "",
                batter: "-",

                matches: "-",
                teamFlag: "",
                maidens: "-",
                wickets: "-",
                team: "-",
            });
        }

        return transformedData?.slice(0, 3);
    }
    return transformedData;
};

export const transformDataMap: Record<
    string,
    (data: ApiResponse, dimension: any) => any
> = {
    sport_cricket_series_key_batsmen: transformMostRunsData,

    sport_cricket_series_highest_individual_score_batsmen:
        transformHighestIndividualRunsData,

    sport_cricket_series_key_bowlers: transformWicketData,

    sport_cricket_series_best_strike_rate_batsmen: transformStrikeRateData,

    sport_cricket_series_best_batting_averages_batsmen:
        transformBestBattingAverageData,

    sport_cricket_series_most_hundreds_batsmen: transformMostHundreds,
    sport_cricket_series_most_fifties_batsmen: transformMostFiftees,

    sport_cricket_series_most_maiden_bowler: transformMaidenBowlers,

    sport_cricket_series_best_economy_bowler: transformEconomyData,

    sport_cricket_series_best_strike_rate_bowler: transformStrikeData,

    sport_cricket_series_most_five_wickets_bowler: transformMostFiveWicketData,

    sport_cricket_series_most_four_wickets_bowler: transformMostFourWicketData,
    sport_cricket_series_best_bowling_figures_bowler:
        transformBestBowlingFiguresData,

    sport_cricket_series_most_dismissals_keeper: transformMostDismissals,

    sport_cricket_series_most_dismissals_in_match_keeper:
        transformMostDismissalsInIPL,

    sport_cricket_series_most_catches_fielder: transformMostCatches,

    sport_cricket_series_most_catches_in_match_fielder:
        transformMostCatchesInIPL,

    sport_cricket_series_most_sixes_batsmen: transformMostSixes,
    sport_cricket_series_most_fours_batsmen: transformMostFours,
};
